/* Dion Wear 2.0 collection page.
   Native Shopify filters/sort, updated in place with the Section Rendering API.
   Without JS the forms and links still work as normal GET navigation. */
(() => {
  const root = document.querySelector('[data-dw-coll]');
  if (!root || root.dataset.dwReady) return;
  root.dataset.dwReady = '1';

  const sectionId = root.dataset.sectionId;
  const drawer = root.querySelector('[data-dw-drawer]');
  let controller = null;

  // ---- URL helpers -------------------------------------------------------
  const paramsFromForm = (form) => {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (v === '' && k.startsWith('filter.v.price')) continue;
      params.append(k, v);
    }
    return params;
  };
  const urlFor = (params) => {
    const qs = params.toString();
    return location.pathname + (qs ? '?' + qs : '');
  };
  const sectionUrl = (url) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.toString();
  };

  // ---- Fetch + swap ------------------------------------------------------
  const fetchSection = async (url) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const res = await fetch(sectionUrl(url), { signal: controller.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const openIds = (scope) => [...scope.querySelectorAll('details[open][id]')].map((d) => d.id);

  const swapRegions = (doc, names) => {
    const focusedId = document.activeElement && document.activeElement.id;
    names.forEach((name) => {
      const cur = root.querySelector(`[data-dw-region="${name}"]`);
      const next = doc.querySelector(`[data-dw-region="${name}"]`);
      if (!cur || !next) return;
      const keep = openIds(cur);
      const scroll = cur.scrollTop;
      cur.replaceWith(next);
      keep.forEach((id) => { const d = next.querySelector('#' + CSS.escape(id)); if (d) d.open = true; });
      next.scrollTop = scroll;
    });
    if (focusedId) {
      const el = document.getElementById(focusedId);
      if (el) el.focus({ preventScroll: true });
    }
  };

  const setBusy = (on) => root.classList.toggle('is-loading', on);

  const render = async (url, { push = true, regions = ['count', 'bar', 'results', 'drawer', 'applycount'], scroll = false } = {}) => {
    setBusy(true);
    try {
      const doc = await fetchSection(url);
      swapRegions(doc, regions);
      if (push) history.pushState({ dw: true }, '', url);
      if (scroll) {
        const top = root.querySelector('[data-dw-region="bar"]').getBoundingClientRect().top + scrollY - 80;
        if (scrollY > top) scrollTo({ top, behavior: 'smooth' });
      }
    } catch (e) {
      if (e.name !== 'AbortError') location.href = url; // fall back to a normal page load
    } finally {
      setBusy(false);
    }
  };

  // ---- Desktop: apply on change -----------------------------------------
  root.addEventListener('change', (e) => {
    const form = e.target.form;
    if (!form) return;
    if (form.dataset.dwForm === 'desktop') {
      render(urlFor(paramsFromForm(form)), { scroll: true });
    } else if (form.dataset.dwForm === 'drawer') {
      // Preview the result count without touching the open drawer
      previewDrawer(form);
    }
  });

  root.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.dataset || !form.dataset.dwForm) return;
    e.preventDefault();
    const url = urlFor(paramsFromForm(form));
    if (form.dataset.dwForm === 'drawer') closeDrawer();
    render(url, { scroll: true });
  });

  // Price inputs: apply on Enter inside desktop dropdowns
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const open = root.querySelector('details[data-dw-drop][open]');
      if (open) { open.open = false; open.querySelector('summary').focus(); }
    }
  });

  // Chips, clear-all, empty-state links
  root.addEventListener('click', (e) => {
    const a = e.target.closest('a[data-dw-link]');
    if (a) { e.preventDefault(); render(a.href, { scroll: true }); return; }

    const clear = e.target.closest('a[data-dw-clear]');
    if (clear) {
      e.preventDefault();
      closeDrawer();
      render(clear.href, { scroll: true });
    }
  });

  // One desktop dropdown open at a time; close on outside click
  root.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!d.matches || !d.matches('details[data-dw-drop]') || !d.open) return;
    root.querySelectorAll('details[data-dw-drop][open]').forEach((o) => { if (o !== d) o.open = false; });
  }, true);
  document.addEventListener('click', (e) => {
    if (e.target.closest('details[data-dw-drop]')) return;
    root.querySelectorAll('details[data-dw-drop][open]').forEach((o) => { o.open = false; });
  });

  // ---- Mobile drawer -----------------------------------------------------
  let previewTimer;
  const previewDrawer = (form) => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(async () => {
      try {
        const doc = await fetchSection(urlFor(paramsFromForm(form)));
        swapRegions(doc, ['applycount']);
      } catch (e) { /* count preview is optional */ }
    }, 200);
  };

  const openDrawer = () => {
    if (!drawer) return;
    if (typeof drawer.showModal === 'function') drawer.showModal();
    else drawer.setAttribute('open', '');
    document.documentElement.classList.add('dw-noscroll');
  };
  function closeDrawer() {
    if (!drawer || !drawer.open) return;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
  }
  if (drawer) {
    drawer.addEventListener('close', () => document.documentElement.classList.remove('dw-noscroll'));
    drawer.addEventListener('click', (e) => { if (e.target === drawer) closeDrawer(); }); // backdrop
  }
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-dw-open-drawer]')) openDrawer();
    if (e.target.closest('[data-dw-close-drawer]')) {
      // Discard un-applied drawer changes by restoring the current state
      closeDrawer();
      render(location.href, { push: false, regions: ['drawer', 'applycount'] });
    }
  });

  // ---- Back / forward ----------------------------------------------------
  window.addEventListener('popstate', () => {
    closeDrawer();
    render(location.href, { push: false });
  });
})();
